import React, {useEffect, useState} from 'react';
import {Box, Button, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ProfileSectionHeading from '../ProfileSectionHeading';
import Typography from '@mui/material/Typography';
import {DatePicker} from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {resetUpdatingStatus, retrieveEthnicity} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import {useParams} from 'react-router-dom';
import {
    DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS,
    PROFILE_SECTIONS_KEYS
} from '../../../../../constants/ProfileSectionsKeys';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import {toDateMoment, toDateOnly} from '../../../../../utility/dateUtil';
import MaskedValue from '../../../../../shared-components/MaskedValue';

const PASSPORT_DATE_KEYS = new Set(['issueDate', 'expiryDate', 'visaExpiryDate']);

const PassportFields = (
    {
        passports,
        setPassports,
        countries,
        setDeletedPassportIds,
        deletedPassportIds,
        readonly,
        asylymStatus,
        student
    }) => {
    const [editIndex, setEditIndex] = useState(null);
    const [editPassportData, setEditPassportData] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [newPassportData, setNewPassportData] = useState({});
    const [errors, setErrors] = useState({});
    const {studentId} = useParams();
    const {updatingSuccessful, currentSchool, selectedProfile} = useSelector(state => state.profilesInfo);
    const dispatch = useDispatch();
    const {data: passportDescriptionData} = useGetProfileDescriptionQuery(PROFILE_SECTIONS_KEYS.passport);
    const passportDescription = passportDescriptionData?.value ??
        DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.passport];

    useEffect(() => {
        showNotification(updatingSuccessful);
        if (updatingSuccessful) {
            dispatch(retrieveEthnicity({studentId}));
        }
        dispatch(resetUpdatingStatus());
    }, [updatingSuccessful]);

    const showNotification = (success) => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Ethnicity passport updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update ethnicity passport', severity: 'error'
        })
    }[success]());

    const handleCancel = () => {
        setIsAddMode(false);
        setNewPassportData({});
    };

    const handleSave = () => {
        newPassportData.isNew = true;
        setPassports([newPassportData, ...passports]);
        setIsAddMode(false);
        setNewPassportData({});
    };

    const normalizeFieldValue = (value, key) =>
        PASSPORT_DATE_KEYS.has(key) ? toDateOnly(value) : value;

    const handleChange = (value, key) => {
        let passportData = JSON.parse(JSON.stringify(newPassportData));
        passportData[key] = normalizeFieldValue(value, key);
        setNewPassportData(passportData);
    };

    const handleEditChange = (value, key) => {
        let passportData = JSON.parse(JSON.stringify(editPassportData));
        passportData[key] = normalizeFieldValue(value, key);
        setEditPassportData(passportData);
    };

    const handleEditCancel = () => {
        setEditIndex(null);
        setEditPassportData({});
    };

    const handleEditSave = () => {
        let editedPassports = JSON.parse(JSON.stringify(passports));
        let passport = editedPassports.find(e => e.id === editPassportData.id);
        passport.nationCode = editPassportData.nationCode;
        passport.passportNumber = editPassportData.passportNumber;
        passport.visaNumber = editPassportData.visaNumber;
        passport.nameOnPassport = editPassportData.nameOnPassport;
        passport.issueDate = editPassportData.issueDate;
        passport.expiryDate = editPassportData.expiryDate;
        passport.visaExpiryDate = editPassportData.visaExpiryDate;

        if (!passport.isNew) {
            passport.isEdited = true;
        }

        setPassports(editedPassports);

        setEditIndex(null);
        setEditPassportData({});
    };

    const handleStartEditing = (index) => {
        setEditIndex(index);
        setEditPassportData(passports.find(el => el.id === index));
    };

    const handleDeletePassport = (passportId) => {
        const deletedPassport = passports.find(e => e.id === passportId);
        setPassports(passports.filter(e => e.id !== passportId));
        if (!deletedPassport.isNew) {
            setDeletedPassportIds([passportId, ...deletedPassportIds]);
        }
    };

    const handleError = (errorMessage, key) => {
        let newErrors = {...errors};
        newErrors[key] = errorMessage;
        setErrors(newErrors)
    }

    const isErrorExist = () => Object.values(errors).some(value => value !== null);

    const buildCountryOptions = () => {
        let filteredCountries = asylymStatus && asylymStatus !== 'NOT_APPLICABLE'
            ? countries.filter(e => e.code !== 'GBR')
            : countries;
        return filteredCountries?.map(({code, description}) => (
            <MenuItem key={code} value={code}>
                {description}
            </MenuItem>
        ));
    };

    const countryName = code => countries?.find(country => country.code === code)?.description ?? code ?? '';
    const shownDate = value => (value ? toDateMoment(value)?.format('D MMM YYYY') : '');

    // Maria, 18 October 2026: one form, in outlined boxes, for adding a passport or changing one
    const passportForm = ({data, onChange, onSave, onCancel, saveLabel, testId}) => (
        <Box data-testid={testId} sx={{border: '1px solid #dfe5ec', borderRadius: 2, p: 2, my: 1.5, backgroundColor: '#fafbfc'}}>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))'}, gap: 2}}>
                <TextField
                    select
                    size="small"
                    required
                    label="Nationality"
                    value={data.nationCode ?? ''}
                    onChange={(event) => onChange(event.target.value, 'nationCode')}
                    // Maria, 18 October 2026: why British is missing from the list
                    helperText={asylymStatus && asylymStatus !== 'NOT_APPLICABLE'
                        ? `British is not in the list, as ${student.name} has refugee or asylum status.`
                        : ''}
                >
                    {buildCountryOptions()}
                </TextField>
                <TextField size="small" label="Passport Number" value={data.passportNumber ?? ''}
                           onChange={(event) => onChange(event.target.value, 'passportNumber')}/>
                <TextField size="small" label="Name On Passport" value={data.nameOnPassport ?? ''}
                           onChange={(event) => onChange(event.target.value, 'nameOnPassport')}/>
                <DatePicker
                    label="Issue Date"
                    value={toDateMoment(data.issueDate)}
                    onChange={(newValue) => onChange(newValue, 'issueDate')}
                    onError={e => handleError(e, 'issueDate')}
                    maxDate={toDateMoment(data.expiryDate)}
                    format={'D-MMM-YYYY'}
                    slotProps={{textField: {size: 'small'}}}
                />
                <DatePicker
                    label="Expiry Date"
                    value={toDateMoment(data.expiryDate)}
                    onChange={(newValue) => onChange(newValue, 'expiryDate')}
                    onError={e => handleError(e, 'expiryDate')}
                    minDate={toDateMoment(data.issueDate)}
                    format={'D-MMM-YYYY'}
                    slotProps={{textField: {size: 'small'}}}
                />
                {data.nationCode !== 'GBR' && (
                    <TextField size="small" label="Visa Number" value={data.visaNumber ?? ''}
                               onChange={(event) => onChange(event.target.value, 'visaNumber')}/>
                )}
                {data.nationCode !== 'GBR' && (
                    <DatePicker
                        label="Visa Expiry Date"
                        value={toDateMoment(data.visaExpiryDate)}
                        onChange={(newValue) => onChange(newValue, 'visaExpiryDate')}
                        onError={e => handleError(e, 'visaExpiryDate')}
                        format={'D-MMM-YYYY'}
                        slotProps={{textField: {size: 'small'}}}
                    />
                )}
            </Box>
            <Stack direction="row" gap={1} sx={{mt: 2}}>
                <Button variant="outlined" onClick={onCancel}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={!data.nationCode || isErrorExist()}>{saveLabel}</Button>
            </Stack>
        </Box>
    );

    const editing = passports?.find(passport => passport.id !== undefined && passport.id === editIndex);

    return <Box>
        {/* Maria, 18 October 2026: less space above the heading */}
        <ProfileSectionHeading sub sx={{mt: 0.5}}>Passport Details</ProfileSectionHeading>
        {!readonly && (
            <Typography variant="body1" sx={{mb: 0.75}}>
                {formatProfileDescription(
                    passportDescription,
                    {
                        schoolName: currentSchool.name,
                        studentName: student.name,
                        hisHer: getHisHerPronoun(selectedProfile.gender),
                    })
                }
            </Typography>
        )}
        {/* Maria, 18 October 2026: the passports in a short list, each with its own Edit and Delete */}
        {passports?.length > 0 && (
            <TableContainer sx={{border: '1px solid #dfe5ec', borderRadius: 2}}>
                {/* Maria, 18 October 2026: shorter rows, especially the heading row */}
                <Table size="small" data-testid="passport-list" sx={{'& th, & td': {py: 0.5, lineHeight: 1.3}}}>
                    <TableHead>
                        <TableRow sx={{backgroundColor: '#e3f5f1'}}>
                            <TableCell sx={{fontWeight: 600}}>Nationality</TableCell>
                            <TableCell sx={{fontWeight: 600}}>Passport Number</TableCell>
                            <TableCell sx={{fontWeight: 600}}>Name On Passport</TableCell>
                            <TableCell sx={{fontWeight: 600}}>Issue Date</TableCell>
                            <TableCell sx={{fontWeight: 600}}>Expiry Date</TableCell>
                            <TableCell sx={{fontWeight: 600}}>Visa</TableCell>
                            {!readonly && <TableCell/>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {passports.map((passport, index) => (
                            <TableRow key={passport.id ?? `new-${index}`} sx={{'&:last-of-type td': {borderBottom: 0}}}>
                                <TableCell sx={{fontWeight: 600}}>{countryName(passport.nationCode)}</TableCell>
                                <TableCell>
                                    <MaskedValue
                                        key={`${passport.id}-${editIndex === passport.id}`}
                                        value={passport.passportNumber}
                                        label="Passport Number"
                                    />
                                </TableCell>
                                <TableCell>{passport.nameOnPassport}</TableCell>
                                <TableCell>{shownDate(passport.issueDate)}</TableCell>
                                <TableCell>{shownDate(passport.expiryDate)}</TableCell>
                                <TableCell>
                                    {passport.nationCode === 'GBR'
                                        ? ''
                                        : [passport.visaNumber, passport.visaExpiryDate ? `expires ${shownDate(passport.visaExpiryDate)}` : '']
                                            .filter(Boolean).join(', ')}
                                </TableCell>
                                {!readonly && (
                                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                        {passport.id !== undefined && (
                                            <Button size="small" variant="outlined" sx={{mr: 1, minWidth: 0, px: 1.5}}
                                                    onClick={() => handleStartEditing(passport.id)}>Edit</Button>
                                        )}
                                        <Button size="small" variant="outlined" color="error" sx={{minWidth: 0, px: 1.5}}
                                                onClick={() => (passport.id !== undefined
                                                    ? handleDeletePassport(passport.id)
                                                    : setPassports(passports.filter(item => item !== passport)))}>Delete</Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
        {!readonly && editing && editPassportData && passportForm({
            data: editPassportData,
            onChange: handleEditChange,
            onSave: handleEditSave,
            onCancel: handleEditCancel,
            saveLabel: 'Save',
            testId: 'passport-edit'
        })}
        {!readonly && isAddMode && passportForm({
            data: newPassportData,
            onChange: handleChange,
            onSave: handleSave,
            onCancel: handleCancel,
            saveLabel: 'Add',
            testId: 'passport-add'
        })}
        {!readonly && !isAddMode && !editing && (
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon/>} sx={{mt: 1.5}} onClick={() => setIsAddMode(true)}>
                Add passport details
            </Button>
        )}
    </Box>;
};

PassportFields.propTypes = {
    passports: PropTypes.array,
    countries: PropTypes.array,
    deletedPassportIds: PropTypes.array,
    setPassports: PropTypes.func,
    setDeletedPassportIds: PropTypes.func,
    readonly: PropTypes.bool,
    asylymStatus: PropTypes.string,
    student: PropTypes.object,
};

export default PassportFields;
